// Apolo IDE — S5 · Keep Rate (2ª via da eval keystone).
//
// "Keep Rate" = fração do código GERADO pelo agente que SOBREVIVE no codebase
// após 1h/1d/7d. É a contraparte de produção do benchmark (oracle pass-rate):
// se o código que o agente escreveu continua lá depois de horas/dias, ele foi
// útil; se foi revertido/reescrito, não foi. Atribuição de diff vem do Glass Box
// (tool calls write_file/apply_edit). Tudo LOCAL, por timestamp.
//
// PRIVACIDADE (E4-WS1-T04): a amostra guarda só HASHES das linhas geradas +
// path + timestamps — NUNCA o conteúdo bruto. A sobrevivência é medida por
// overlap de hash contra o arquivo atual. Sem PII.

import { KEEP_RATE_WINDOWS, KEEP_RATE_WINDOW_MS } from './s5Contracts.js';

/** ≥50% das linhas geradas ainda presentes ⇒ o diff "sobreviveu". */
export const SURVIVAL_THRESHOLD = 0.5;

/** Hash estável (djb2) — content-addressed, não reversível p/ o conteúdo. */
export function hashLine(s) {
  const str = typeof s === 'string' ? s.trim() : '';
  if (!str) return '';
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return `h${(h >>> 0).toString(36)}`;
}

function hashSet(lines) {
  const set = new Set();
  for (const l of Array.isArray(lines) ? lines : []) {
    const h = hashLine(l);
    if (h) set.add(h);
  }
  return set;
}

/** Linhas ADICIONADAS por uma tool call (atribuição de diff via Glass Box). */
export function extractAddedLines(toolName, args = {}) {
  if (toolName === 'write_file') return String(args.content || '').split('\n');
  if (toolName === 'apply_edit') return String(args.new_string || '').split('\n');
  return [];
}

/**
 * Cria a amostra de Keep Rate a partir de um diff atribuído. Guarda só hashes.
 * @param {{ diffId:string, file:string, addedLines:string[], generatedAt:number }} diff
 * @returns {{ diffId:string, file:string, generatedAt:number, lineHashes:string[], survived:{['1h']:boolean,['1d']:boolean,['7d']:boolean} }}
 */
export function trackDiff({ diffId, file, addedLines, generatedAt }) {
  return {
    diffId: String(diffId),
    file: String(file || ''),
    generatedAt: Number(generatedAt) || 0,
    lineHashes: [...hashSet(addedLines)],
    survived: { '1h': false, '1d': false, '7d': false },
  };
}

/** Atalho: tool call → amostra trackável (ou null se não for write/edit). */
export function attributeDiff(toolName, args, { diffId, generatedAt }) {
  const added = extractAddedLines(toolName, args);
  if (added.length === 0) return null;
  const file = args && (args.path || args.filePath || args.file);
  return trackDiff({ diffId, file, addedLines: added, generatedAt });
}

/**
 * Mede a sobrevivência de UMA amostra contra o conteúdo ATUAL do arquivo.
 * Para cada janela já vencida (elapsed ≥ window), survived = fração de hashes
 * ainda presentes ≥ threshold. `currentLines === null` ⇒ arquivo apagado ⇒ 0.
 *
 * @param {object} sample
 * @param {{ now:number, currentLines:string[]|null, threshold?:number }} ctx
 */
export function measureSample(sample, { now, currentLines, threshold = SURVIVAL_THRESHOLD }) {
  const elapsed = now - sample.generatedAt;
  const present = currentLines == null ? new Set() : hashSet(currentLines);
  const total = sample.lineHashes.length || 1;
  const kept = sample.lineHashes.filter((h) => present.has(h)).length;
  const frac = kept / total;
  const survived = { ...sample.survived };
  for (const w of KEEP_RATE_WINDOWS) {
    if (elapsed >= KEEP_RATE_WINDOW_MS[w]) survived[w] = frac >= threshold;
  }
  return { ...sample, survived, survivalFraction: frac };
}

/**
 * Keep Rate agregado numa janela = média de `survived[window]` sobre as amostras
 * ELEGÍVEIS (já vencidas àquela janela). null se nenhuma elegível ainda.
 */
export function keepRate(samples, window, now = Date.now()) {
  const ms = KEEP_RATE_WINDOW_MS[window];
  if (!ms) return null;
  const eligible = (Array.isArray(samples) ? samples : []).filter((s) => now - s.generatedAt >= ms);
  if (eligible.length === 0) return null;
  const kept = eligible.filter((s) => s.survived && s.survived[window]).length;
  return kept / eligible.length;
}

/**
 * Une a 2ª via (produção) com a 1ª (benchmark): keystone combinado. Ambos são
 * frações 0..1; o oracle pass-rate do benchmark e o Keep Rate de produção
 * convergem no mesmo eixo "o trabalho do agente vale?".
 */
export function combinedEval({ productionKeepRate, benchmarkPassRate, weight = 0.5 }) {
  const p = Number.isFinite(productionKeepRate) ? productionKeepRate : null;
  const b = Number.isFinite(benchmarkPassRate) ? benchmarkPassRate : null;
  if (p == null && b == null) return null;
  if (p == null) return b;
  if (b == null) return p;
  const w = Math.min(1, Math.max(0, weight));
  return p * w + b * (1 - w);
}

/**
 * (Opcional/local) Satisfação semântica: pergunta a um modelo local se o diff
 * ainda cumpre seu propósito. Gancho documentado — NÃO requerido no green path
 * (a sobrevivência determinística é o sinal core). Sem chatFn ⇒ null.
 */
export async function semanticSatisfaction(summary, { chatFn } = /** @type {{ chatFn?: Function }} */ ({})) {
  if (typeof chatFn !== 'function') return null;
  try {
    const res = await chatFn({
      messages: [
        { role: 'system', content: 'Responda só com um número 0..1: o quanto este código ainda cumpre seu propósito.' },
        { role: 'user', content: String(summary || '').slice(0, 2000) },
      ],
      options: { temperature: 0 },
    });
    const text = (res && (res.content || res.text)) || '';
    const m = String(text).match(/0?\.\d+|[01]/);
    return m ? Math.min(1, Math.max(0, Number(m[0]))) : null;
  } catch {
    return null;
  }
}

// ───────── Store local (alimenta o dashboard E5) ─────────
const _samples = [];

export function recordDiff(diff) {
  if (!diff) return null;
  const sample = diff.lineHashes ? diff : attributeDiffSampleFrom(diff);
  if (sample) _samples.push(sample);
  return sample;
}
function attributeDiffSampleFrom(d) {
  if (d && d.addedLines) return trackDiff(d);
  return null;
}
export function getKeepRateSamples() {
  return _samples.slice();
}
/** Re-mede todas as amostras lendo o conteúdo atual (readFile(path)->string|null). */
export async function measureAll({ now = Date.now(), readFile }) {
  for (let i = 0; i < _samples.length; i += 1) {
    const s = _samples[i];
    const content = typeof readFile === 'function' ? await readFile(s.file) : null;
    const currentLines = content == null ? null : String(content).split('\n');
    _samples[i] = measureSample(s, { now, currentLines });
  }
  return getKeepRateSamples();
}
export function __resetKeepRate() {
  _samples.length = 0;
}
